import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';

import type {
  CreateEmailRuleInput,
  UpdateEmailRuleInput,
  DeleteEmailRuleInput,
  CreateEmailRuleEventAndLinkInput,
  TestInboundEmailInput,
} from '@erp/rpc';

export function useEmailRules() {
  return useQuery(
    orpc.communications.emailRules.list.queryOptions({ input: {} }),
  );
}

export function useEmailRule(id: string) {
  return useQuery(
    orpc.communications.emailRules.get.queryOptions({ input: { id } }),
  );
}

export function useCreateEmailRule() {
  const queryClient = useQueryClient();

  return useMutation(
    orpc.communications.emailRules.create.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.communications.emailRules.list.key(),
        });
      },
    }),
  );
}

export function useUpdateEmailRule() {
  const queryClient = useQueryClient();

  return useMutation(
    orpc.communications.emailRules.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.communications.emailRules.list.key(),
        });
      },
    }),
  );
}

export function useDeleteEmailRule() {
  const queryClient = useQueryClient();

  return useMutation(
    orpc.communications.emailRules.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.communications.emailRules.list.key(),
        });
      },
    }),
  );
}

export function useTestInboundEmailRuleMatching() {
  return useMutation(
    orpc.communications.emailRules.testInbound.mutationOptions(),
  );
}

export function useCreateEmailRuleEventAndLink() {
  const queryClient = useQueryClient();

  return useMutation(
    orpc.communications.emailRules.events.createAndLink.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.communications.emailRules.list.key(),
        });
      },
    }),
  );
}

export function useListWorkflowsTriggerableByInboundEmail(
  inboundEmailId: string,
  options?: { enabled?: boolean },
) {
  return useQuery(
    orpc.communications.emailRules.listWorkflowsTriggerableByInboundEmail.queryOptions(
      {
        input: { inboundEmailId },
        enabled: options?.enabled ?? Boolean(inboundEmailId),
      },
    ),
  );
}

export function useInboundEmailWorkflowRunSummary() {
  return useMutation(
    orpc.communications.emailRules.getInboundEmailWorkflowRunSummary.mutationOptions(),
  );
}

export function useListInboundEmailWorkflowRuns(
  inboundEmailId: string,
  options?: { enabled?: boolean },
) {
  return useQuery(
    orpc.communications.emailRules.listInboundEmailWorkflowRuns.queryOptions({
      input: { inboundEmailId },
      enabled: options?.enabled ?? Boolean(inboundEmailId),
    }),
  );
}

// Convenience typed wrappers
export type {
  CreateEmailRuleInput,
  UpdateEmailRuleInput,
  DeleteEmailRuleInput,
  CreateEmailRuleEventAndLinkInput,
  TestInboundEmailInput,
};
